import React, { useEffect, useState } from 'react';
import { LayoutBackground } from '../../components/Layout/LayoutBackground/LayoutBackground.component';
import Navbar from '../../components/Layout/Navbar/Navbar.component';
import { useStyles } from './Dashboard.styles';
import { DashboardHeader } from './DashboardHeader/DashboardHeader.component';
import { useHistory } from 'react-router-dom';
import { getDashboardTiles } from '../../services/exams/assessmentSession/assessments.service';
import { AxiosResponse } from 'axios';
import { useLayoutContext } from '../../Context/Layout.context';
import { ExamTaskGroupSolutionCard } from './ExamTaskGroupSolutionCard/ExamTaskGroupSolutionCard.component';
import {
  AssessmentSessionState,
  DashboardTile,
  DashboardType,
  DataForExaminerTreeMap,
  Tile,
  TileType,
} from './Dashboard.types';
import { getErrorPageUrl } from '../../utils/routerUrls';
import { ControlledAccordions } from '../../components/Accordion/Accordion.component';
import _ from 'lodash';
import { Typography } from '@mui/material';
import { toTitleCase } from '../../utils/toTitleCase';
import SessionDetailsModal from '../../components/EditSession/SessionDetailsModal/SessionDetailsModal.component';
import useSessionUser from '../../hooks/useSessionUser';
import { ActionMenuItem } from '../../components/ActionsMenu/ActionsMenu.types';
import { AdjustmentsModal } from '../../components/Adjustments/AdjustmentsModal.component';
import { assessmentSessionActions } from '../../reducers/assessmentSessionReducer/assessmentSessionSlice';
import { useDispatch } from 'react-redux';
import useDocumentTitle from '../../hooks/DocumentTitle/documentTitle';
import { SignalRContext } from '../../Context/SignalR.context';
import { CallbackMethodNames } from '../../models/signalR.types';
import { useExaminer } from '../../Context/Examiner.context';
import { ExaminerTreemapModal } from '../ExaminerTreemap/ExaminerTreemapModal.component';
import { AnnotationsAssignmentModal } from '../AnnotationsAssignment/AnnotationsAssignmentModal.component';
import { SkipLink } from '../../components/SkipLink/SkipLink.component';
import { filtersActions } from '../../reducers/filtersReducer/filtersSlice';
import { DataInfoType } from '../../reducers/filtersReducer/filterSlice.types';

const Dashboard: React.FC = () => {
  const styles = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { isLoader, showLoader } = useLayoutContext();
  const { userAssessmentSessionInfo, reloadAssessmentSession } = useSessionUser();

  const [assessmentSessionData, setAssessmentSessionData] = useState<DashboardTile[]>([]);
  const [isSessionEditorModalOpened, setIsSessionEditorModalOpened] = useState<boolean>(false);
  const [examinerTreemapModalOpen, setExaminerTreemapModalOpen] = useState<boolean>(false);
  const [annotationsAssignmentModal, setAnnotationsAssignmentModal] = useState<boolean>(false);
  const [adjustmentsModalOpen, setAdjustmentsModalOpen] = useState<boolean>(false);
  const [adjustmentModalProps, setAdjustmentModalProps] =
    useState<{ title: string; sheetId: string; isExamSheetConfigured: boolean }>();

  const [activeDashboardtype, setActiveDashboardtype] = useState<DashboardType>('assessment');
  const [assesmentSessionSuspendedId, setAssesmentSessionSuspendedId] = useState<string>();
  const [dataForExaminerTreeMap, setDataForExaminerTreeMap] = useState<DataForExaminerTreeMap>();
  const [selectedSheetId, setSelectedSheetId] = useState<string>('');

  const [isSkipLink, setIsSkipLink] = useState<boolean>(false);
  const examinerIsWorking = sessionStorage.getItem('startWork');
  const { setExaminer } = useExaminer();

  useDocumentTitle('Panel egzaminatora');

  useEffect(() => {
    if (examinerIsWorking === 'true') {
      sessionStorage.removeItem('startWork');
    }
  }, []);

  useEffect(() => {
    dispatch(filtersActions.resetFilter(DataInfoType.AssessmentScoringInfo));
    dispatch(filtersActions.resetFilter(DataInfoType.AssessmentControlInfo));
    dispatch(filtersActions.resetFilter(DataInfoType.AssessmentTrainingInfo));
    dispatch(filtersActions.resetFilter(DataInfoType.AssessmentQualificationInfo));
  }, []);

  SignalRContext.useSignalREffect(
    CallbackMethodNames.AssessmentSessionSuspended,
    (message) => {
      setAssesmentSessionSuspendedId(message.assessmentSessionId);
    },
    [],
  );

  useEffect(() => {
    dispatch(assessmentSessionActions.updateAssessmentSession(null));
  }, []);

  useEffect(() => {
    setExaminer(undefined);
  }, []);

  const refreshData = () => {
    const isTrainingOnly = activeDashboardtype === 'training';

    showLoader(true);
    getDashboardTiles('', isTrainingOnly)
      .then((result: AxiosResponse<Tile>) => {
        setAssessmentSessionData(result.data.dashboardTiles);

        if (
          result.data.dashboardTiles.find((e) => e.assessmentSessionId === assesmentSessionSuspendedId)
            ?.assessmentSessionStateId === AssessmentSessionState.Opened
        ) {
          setAssesmentSessionSuspendedId(undefined);
        }
      })
      .catch(() => {
        showLoader(false);
        history.push(getErrorPageUrl(500, 'dashboard'));
      })
      .finally(() => {
        showLoader(false);
      });
  };

  const isSessionUserLoaded = !_.isEmpty(userAssessmentSessionInfo);

  useEffect(() => {
    if (!isSessionUserLoaded) return;

    refreshData();
  }, [activeDashboardtype, isSessionUserLoaded]);

  const handleSwitchDashboardType = (activeDashboardType: DashboardType) => {
    setActiveDashboardtype(activeDashboardType);
  };

  const sessionsBySheetId = Object.entries(_.groupBy(assessmentSessionData, 'examSheetId'));

  const generateTextSummary = (tile: DashboardTile) => {
    return `${toTitleCase(tile.dashboardSessionName)} / ${toTitleCase(tile.dashboardExamName)} / ${
      tile.dashboardSheetCode ? tile.dashboardSheetCode.toUpperCase() : ''
    }`;
  };

  const openSessionEditorModal = async (assessmentSessionId: string) => {
    await reloadAssessmentSession(assessmentSessionId);

    setIsSessionEditorModalOpened(true);
  };

  const openExaminerTreeMap = async (
    sheetId: string,
    assessmentSessionName: string,
    subject: string,
    assessmentSessionId: string,
    isControlProcessActive: boolean,
    isExamSheetConfigured: boolean,
  ) => {
    await reloadAssessmentSession(assessmentSessionId);

    setDataForExaminerTreeMap({
      sheetId: sheetId,
      assessmentSessionName: assessmentSessionName,
      subject: subject,
      assessmentSessionId: assessmentSessionId,
      isExamSheetConfigured: isExamSheetConfigured ? 'true' : 'false',
      isControlProcessActive: isControlProcessActive ? 'true' : 'false',
    });
    setExaminerTreemapModalOpen(true);
  };

  const openAnnotations = async (sheetId: string, assessmentSessionId: string) => {
    setSelectedSheetId(sheetId);
    await reloadAssessmentSession(assessmentSessionId);

    setAnnotationsAssignmentModal(true);
  };

  const openAdjustmentsModal = async (
    sheetId: string,
    adjustmentsModalTitle: string,
    assessmentSessionId: string,
    isExamSheetConfigured: boolean,
  ) => {
    await reloadAssessmentSession(assessmentSessionId);

    setAdjustmentModalProps({
      title: adjustmentsModalTitle,
      sheetId: sheetId,
      isExamSheetConfigured: isExamSheetConfigured,
    });
    setAdjustmentsModalOpen(true);
  };

  const getMenuItems = (
    sheetId: string,
    assessmentSessionName: string,
    subject: string,
    assessmentSessionId: string,
    assessmentSessionStateId: AssessmentSessionState[],
    isControlProcessActive: boolean,
    isCountryCoordinator: boolean,
    adjustmentsModalTitle: string,
    isExamSheetConfigured: boolean,
  ) => {
    const items: ActionMenuItem[] = [];

    if (isCountryCoordinator) {
      items.push({
        text: 'Edytuj sesję egzaminacyjną',
        onClick: () => openSessionEditorModal(assessmentSessionId),
      });
    }

    items.push({
      text: 'Zarządzaj strukturą egzaminatorów',
      onClick: () =>
        openExaminerTreeMap(
          sheetId,
          assessmentSessionName,
          subject,
          assessmentSessionId,
          isControlProcessActive,
          isExamSheetConfigured,
        ),
    });

    if (isCountryCoordinator && assessmentSessionStateId.some((item) => item !== AssessmentSessionState.Closed)) {
      items.push({
        text: 'Adnotacje',
        onClick: () => openAnnotations(sheetId, assessmentSessionId),
      });
    }

    if (isCountryCoordinator) {
      items.push({
        text: 'Dostosowania',
        onClick: () => openAdjustmentsModal(sheetId, adjustmentsModalTitle, assessmentSessionId, isExamSheetConfigured),
      });
    }

    return items;
  };

  if (window.location.hash) {
    history.replace({
      pathname: window.location.pathname,
      search: window.location.search,
    });
  }

  return (
    <div>
      <SkipLink isSkipLink={isSkipLink} setIsSkipLink={setIsSkipLink} />
      <Navbar dashboardView={true} isSkipLink={isSkipLink} />
      <div className={styles.container}>
        {assessmentSessionData.length > 0 && (
          <Typography
            variant="h1"
            aria-level={1}
            className={styles.h5Style}
            style={{ position: 'relative', left: '-5000px' }}
            aria-hidden={false}
            tabIndex={0}
          >
            {activeDashboardtype === 'training' ? 'Wybór sesji treningowej' : 'Wybór sesji egzaminacyjnej'}
          </Typography>
        )}
        <div style={{ outline: 'none' }} id="main-content">
          <DashboardHeader
            isEmptyResult={!assessmentSessionData.length}
            activeDashboardType={activeDashboardtype}
            onSwitchDashboardType={handleSwitchDashboardType}
          />
        </div>
        {!assessmentSessionData.length && !isLoader ? (
          <Typography variant="h1" className={styles.h5Style} align="center">
            {activeDashboardtype === 'training'
              ? 'Brak sesji treningowych do wyświetlenia'
              : 'Brak sesji egzaminacyjnych do wyświetlenia'}
          </Typography>
        ) : (
          <>
            {isSessionEditorModalOpened && (
              <SessionDetailsModal
                sessionId={null}
                reloadOnClose={refreshData}
                onClose={() => {
                  refreshData();
                  setIsSessionEditorModalOpened(false);
                }}
                disableEditingSessionAndExam={true}
              />
            )}
            {adjustmentsModalOpen && adjustmentModalProps && (
              <AdjustmentsModal
                examSheetId={adjustmentModalProps.sheetId}
                title={adjustmentModalProps.title}
                onClose={() => setAdjustmentsModalOpen(false)}
                importedFromExternalSystem={undefined} //undefined as KK will not have possibility to import from external systems
                isKK
                isConfigured={adjustmentModalProps.isExamSheetConfigured}
              />
            )}
            {examinerTreemapModalOpen && dataForExaminerTreeMap && (
              <ExaminerTreemapModal
                data={dataForExaminerTreeMap}
                examinerMode={true}
                onClose={() => setExaminerTreemapModalOpen(false)}
              />
            )}
            {selectedSheetId && annotationsAssignmentModal && (
              <AnnotationsAssignmentModal
                sheetId={selectedSheetId}
                onClose={() => setAnnotationsAssignmentModal(false)}
              />
            )}
            <div className={styles.overflowContainer}>
              <div className={styles.dashboardContainer}>
                {sessionsBySheetId.map((examSession: [string, DashboardTile[]], index: number) => {
                  const sheetId = examSession[0];
                  const tiles = examSession[1];
                  const isCountryCoordinator = tiles.some((x) => x.tileType === TileType.KK);
                  const isTaskGroupCoordinator = tiles.some((x) => x.tileType === TileType.KGZ);
                  const textSummary = generateTextSummary(tiles[0]);
                  const assessmentSessionId = isTaskGroupCoordinator
                    ? tiles.filter((t) => t.tileType === TileType.KGZ).map((t) => t.assessmentSessionId)[0]
                    : tiles[0].assessmentSessionId;

                  return (
                    <ControlledAccordions
                      expandedByDefault={sessionsBySheetId.length === 1 ? `panel-${textSummary}` : false}
                      textSummary={textSummary}
                      key={`${sheetId}-${index}`}
                      menuItems={
                        isCountryCoordinator || isTaskGroupCoordinator
                          ? getMenuItems(
                              sheetId,
                              tiles[0].assessmentSessionName,
                              tiles[0].examName,
                              assessmentSessionId,
                              tiles.map((item) => item.assessmentSessionStateId),
                              tiles[0].isControlProcessActive,
                              isCountryCoordinator,
                              textSummary,
                              tiles[0].isExamSheetConfigured,
                            )
                          : undefined
                      }
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: 16, textAlign: 'left', color: 'white', margin: 1 }}>
                        Wykaz sesji oceniania
                      </Typography>
                      {tiles.map((assessmentSession) => {
                        return (
                          <ExamTaskGroupSolutionCard
                            suspended={assessmentSession.assessmentSessionId === assesmentSessionSuspendedId}
                            assessmentSession={assessmentSession}
                            key={`${assessmentSession.assessmentSessionId}-${assessmentSession.sessionType}-${assessmentSession.tileState}-${assessmentSession.tileType}`}
                            refresh={refreshData}
                          />
                        );
                      })}
                    </ControlledAccordions>
                  );
                })}
              </div>
            </div>
          </>
        )}
        <LayoutBackground isLoaderHidden={true} isCentralLineHidden={true} dashboardType={activeDashboardtype} />
      </div>
    </div>
  );
};

export default Dashboard;
